import React, {useEffect} from 'react';

import flags, {isHtoEnabled} from 'constants/flags/flags';
import {InView, IntersectionOptions} from 'react-intersection-observer';
import {GENERAL, sendMobileAnalyticsPerSession} from '@optimaxdev/analytics/mobile';

export type FeatureFlagsKeyType = keyof typeof flags | 'hto';

export type PropsType<T = FeatureFlagsKeyType> = {
    flag: T;
    children: React.ReactNode;
    fallback?: React.ReactNode;
    cookies?: {[key: string]: string};
    inViewProps?: IntersectionOptions;
    isInteraction?: boolean;
    fallbackAnalytics?: boolean;
};

type FeaturesType = {
    [key: string]: Function;
};

const features: FeaturesType = {
    ...Object.keys(flags).reduce((acc, key) => {
        return {
            ...acc,
            [key]: () => (flags[key].isEnabled ? flags[key].isEnabled() : false),
        };
    }, {}),
    hto: isHtoEnabled,
};

/**
 * Feature component, accept name of the flag name,
 * feature component as a child and callback if needed
 *
 * @returns {ReactNode} Feature component
 */
export function Feature({children, fallback, flag, cookies}: PropsType) {
    if (!features[flag]) return <>{fallback}</>;

    if (flags[flag]?.getValue && ['B', 'C'].includes(flags[flag].getValue())) {
        return <>{children}</>;
    }

    return <>{features[flag](cookies) ? children : fallback}</>;
}

Feature.defaultProps = {fallback: null, children: null};

/**
 * FeatureWithAnalytics component, accept name of the flag name,
 * feature component as a child and callback if needed
 * FeatureWithAnalytics is used when we want to send GA event on component mount
 *
 * @returns {ReactNode} Feature component
 */
export function FeatureWithAnalytics({
    children,
    fallback,
    flag,
    cookies,
    isInteraction,
}: PropsType<FeatureFlagsKeyType>) {
    useEffect(() => {
        sendMobileAnalyticsPerSession({
            event: GENERAL.EXPERIMENT_INDICATION,
            label: {
                label: String(features[flag]()),
                expName: flag,
            },
            isInteraction,
        });
    }, []);

    if (!features[flag]) return <>{fallback}</>;

    return <>{features[flag](cookies) ? children : fallback}</>;
}

FeatureWithAnalytics.defaultProps = {fallback: null, children: null, isInteraction: true};

/**
 * FeatureWithAnalyticsOnView component, accept name of the flag name and all inView props,
 * feature component as a child and callback if needed
 * FeatureWithAnalyticsOnView is used when we want to send GA event on true view element as DOM element
 * if add fallbackAnalytics, FeatureWithAnalyticsOnView should send GA event if flag is disabled
 * FeatureWithAnalyticsOnView can apply default attributes like className and data/aria labels
 *
 * @returns {ReactNode} Feature component
 */
export function FeatureWithAnalyticsOnView({
    children,
    fallback,
    flag,
    cookies,
    inViewProps,
    isInteraction,
    fallbackAnalytics,
}: PropsType<FeatureFlagsKeyType>) {
    const onChange = (inView): void => {
        if (!inView) return;
        sendMobileAnalyticsPerSession({
            event: GENERAL.EXPERIMENT_INDICATION,
            label: {
                label: String(features[flag](cookies)),
                expName: flag,
            },
            isInteraction,
        });
    };

    if (!features[flag] && !fallbackAnalytics) return <>{fallback}</>;

    return (
        <InView onChange={onChange} {...inViewProps}>
            {features[flag](cookies) ? children : fallback}
        </InView>
    );
}

FeatureWithAnalyticsOnView.defaultProps = {
    fallback: null,
    children: null,
    inViewProps: {threshold: 1, triggerOnce: true},
    isInteraction: true,
    fallbackAnalytics: false,
};

/**
 * Feature hook, accept name of the flag name,
 * @param {string} flag - feature name
 * @returns {boolean} Status of feature
 */
export const useFeature = (flag: FeatureFlagsKeyType) => {
    if (!features[flag]) return false;

    return features[flag]();
};

/**
 * getFeatureWithAnalytics getter, accepts name of the flag name, sends GA event and return flag's value
 * @param {string} flag - feature name
 * @param {boolean} isInteraction - isInteraction
 * @returns {boolean} Status of feature
 */
export const getFeatureWithAnalytics = (flag: FeatureFlagsKeyType, isInteraction = true) => {
    if (!features[flag]) return false;

    if (!__NODEJS__) {
        sendMobileAnalyticsPerSession({
            event: GENERAL.EXPERIMENT_INDICATION,
            label: {
                label: String(features[flag]()),
                expName: flag,
            },
            isInteraction,
        });
    }

    return features[flag]();
};
